import { useMemo, useState } from "react";
import { CircleMinus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Field,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from "@/components/ui/field";
import { segmentedItem, segmentedStrip } from "@/components/ui/segmented";
import { Textarea } from "@/components/ui/textarea";
import { FormActions } from "@jbnl/shared-ui";
import { Keuzelijst } from "./Keuzelijst";
import { zonderVoorvoegsel } from "./naam";
import { tijd } from "./tijd";
import {
  bezet as bezetKeuzes,
  useLosmakenIndeling,
  useVastzettenIndeling,
  useVerwijderenIndeling,
  type OngeplaatstePloeg,
  type PlanningBaan,
  type PlanningDag,
  type PlanningPloeg,
  type PlanningRegel,
} from "./usePlanning";

/** The ploeg a beheerder has picked up and not yet put down. */
export type Verplaatsing = { ploegId: string; naam: string; klasseId: string };

type PloegKandidaat = {
  ploegId: string;
  ploegNaam: string;
  korpsNaam: string;
  isOngeplaatst: boolean;
};

/** The ploeg picker for an open plek: every ploeg of the klasse, unplaced ones first. */
function PloegVeld({
  klasseNaam,
  kandidaten,
  gekozen,
  onKies,
}: {
  klasseNaam: string;
  kandidaten: PloegKandidaat[];
  gekozen: string;
  onKies: (id: string) => void;
}) {
  const { t } = useTranslation(["planning", "common"]);

  return (
    <Field>
      <FieldLabel>{t("cel.ploeg")}</FieldLabel>
      <p className="text-xs text-muted-foreground">{t("cel.ploegUitleg")}</p>
      <Keuzelijst
        naam={t("keuzelijst.ploeg")}
        zoekPlaceholder={(count) => t("keuzelijst.zoekPloegen", { count })}
        items={kandidaten}
        sleutel={(p) => p.ploegId}
        // The whole naam, voorvoegsel and all: what is typed here is searched and
        // not read, so a beheerder who pastes "Jeugdbrandweer Gorinchem" out of a mail
        // still lands on the ploeg the shortened row shows.
        zoekterm={(p) => `${p.ploegNaam} ${p.korpsNaam}`}
        gekozen={gekozen}
        onKies={onKies}
        regel={(p) => (
          <>
            <span className="font-medium">
              {zonderVoorvoegsel(p.ploegNaam)}
            </span>
            <span className="text-muted-foreground">
              {" "}
              · {zonderVoorvoegsel(p.korpsNaam)}
            </span>
            {p.isOngeplaatst && (
              <span className="text-muted-foreground">
                {t("cel.nogNietIngedeeld")}
              </span>
            )}
          </>
        )}
        leeg={t("cel.leeg", { klasse: klasseNaam })}
      />
    </Field>
  );
}

/** What the save will do with the ploeg standing here: ruilen (trade) or overschrijven (bump). */
function BezetterWisselKeuze({
  bezetter,
  kanRuilen,
  actie,
  gekozenNaam,
  onKiesActie,
}: {
  bezetter: NonNullable<PlanningRegel["ploeg"]>;
  kanRuilen: boolean;
  actie: number;
  gekozenNaam: string;
  onKiesActie: (actie: number) => void;
}) {
  const { t } = useTranslation(["planning", "common"]);

  return (
    <FieldSet>
      <FieldLegend variant="label">
        {t("cel.bezet.vraag", { naam: zonderVoorvoegsel(bezetter.naam) })}
      </FieldLegend>

      {kanRuilen ? (
        <>
          <div className={segmentedStrip}>
            <button
              type="button"
              className={segmentedItem}
              aria-pressed={actie === bezetKeuzes.ruilen}
              onClick={() => onKiesActie(bezetKeuzes.ruilen)}
            >
              {t("cel.bezet.ruilen")}
            </button>
            <button
              type="button"
              className={segmentedItem}
              aria-pressed={actie === bezetKeuzes.overschrijven}
              onClick={() => onKiesActie(bezetKeuzes.overschrijven)}
            >
              {t("cel.bezet.overschrijven")}
            </button>
          </div>
          <p className="text-xs text-muted-foreground">
            {actie === bezetKeuzes.ruilen
              ? t("cel.bezet.ruilenUitleg", {
                  naam: zonderVoorvoegsel(bezetter.naam),
                  gekozen: gekozenNaam,
                })
              : t("cel.bezet.overschrijvenUitleg", {
                  naam: zonderVoorvoegsel(bezetter.naam),
                  gekozen: gekozenNaam,
                })}
          </p>
        </>
      ) : (
        // Nothing to trade with: the chosen ploeg holds no place at this niveau, so the
        // one standing here can only lose its own.
        <p className="text-xs text-muted-foreground">
          {t("cel.bezet.alleenOverschrijven", {
            naam: zonderVoorvoegsel(bezetter.naam),
            gekozen: gekozenNaam,
          })}
        </p>
      )}

      {/* A vastzetting is a beheerder's earlier decision, so losing it is said out loud
          rather than discovered afterwards. */}
      {bezetter.isVastgezet && actie === bezetKeuzes.overschrijven && (
        <p className="text-xs text-muted-foreground">
          {t("cel.bezet.vastzettingVervalt", {
            naam: zonderVoorvoegsel(bezetter.naam),
          })}
        </p>
      )}
    </FieldSet>
  );
}

/** The destructive footer for an occupied cell: hand it back, or take the ploeg off altogether. */
function CelVerwijderActies({
  bezetter,
  bezig,
  onLosmaken,
  onVerwijderen,
}: {
  bezetter: NonNullable<PlanningRegel["ploeg"]>;
  bezig: boolean;
  onLosmaken: () => void;
  onVerwijderen: () => void;
}) {
  const { t } = useTranslation(["planning", "common"]);

  return (
    <div className="flex flex-wrap gap-2">
      {bezetter.isVastgezet && (
        <Button variant="outline" disabled={bezig} onClick={onLosmaken}>
          {t("cel.losmaken")}
        </Button>
      )}
      {/* The only control here that takes a ploeg out of the planning, so it is the
          only one that carries an icon - and a minus rather than a prullenbak,
          because nothing is deleted. */}
      <Button variant="outline" disabled={bezig} onClick={onVerwijderen}>
        <CircleMinus aria-hidden />
        {t("cel.verwijderen")}
      </Button>
    </div>
  );
}

/**
 * One cell of the speelschema, opened. A free starttijd and a taken one are the same dialog,
 * because they are the same act: <em>this ploeg plays here</em>.
 *
 * <p>
 * <b>It does not offer a list of destinations.</b> Where a ploeg may go is every starttijd of its
 * klasse across the whole seizoen - hundreds by the finale - and a control listing those is the
 * one ADR-0028 deleted. <em>Verplaatsen</em> instead arms the move and hands the choosing back to
 * the grid, which already draws the schema and which the beheerder is already reading. The armed
 * move survives switching wedstrijddag, so a korps that cannot make the 27th is moved to the 10th
 * by opening the 10th and clicking a cell.
 * </p>
 * <p>
 * <b>A taken cell is a landing place like any other.</b> What it asks extra is what happens to the
 * ploeg standing there - it trades places, or it loses its own - because that is a decision a
 * beheerder makes and not one a screen may make quietly on their behalf.
 * </p>
 */
export function CelDialog({
  seizoenId,
  dagen,
  dag,
  baan,
  regel,
  verplaatst,
  ongeplaatst,
  onVerplaats,
  onClose,
}: {
  seizoenId: string;
  dagen: PlanningDag[];
  dag: PlanningDag;
  baan: PlanningBaan;
  regel: PlanningRegel;
  verplaatst: Verplaatsing | null;
  ongeplaatst: OngeplaatstePloeg[];
  onVerplaats: (ploeg: PlanningPloeg) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation(["planning", "common"]);
  const vastzetten = useVastzettenIndeling(seizoenId);
  const losmaken = useLosmakenIndeling(seizoenId);
  const verwijderen = useVerwijderenIndeling(seizoenId);

  const bezetter = regel.ploeg;

  // Every ploeg entered in this klasse, the ones the planner could not place first. A placed
  // ploeg can be chosen here as well as moved from the cell it stands in - the two are the same
  // act and a beheerder should not have to find the old cell first.
  //
  // The klasse is where it stops. A ploeg plays the klasse its korps entered it in (ADR-0033),
  // so putting one on a baan of another klasse is not overruling the planning but overruling the
  // inschrijving - which #72 says stays on the namens path that already exists (ADR-0022),
  // rather than growing a second way to do it here.
  const kandidaten = useMemo(() => {
    const geplaatst = dagen
      .flatMap((d) => d.banen.filter((b) => b.klasseId === baan.klasseId))
      .flatMap((b) => b.regels)
      .map((r) => r.ploeg)
      .filter((p): p is NonNullable<typeof p> => p !== null)
      .map((p) => ({
        ploegId: p.id,
        ploegNaam: p.naam,
        korpsNaam: p.korpsNaam,
        isOngeplaatst: false,
      }));

    const zonderPlaats = ongeplaatst
      .filter((p) => p.klasseId === baan.klasseId)
      .map((p) => ({
        ploegId: p.ploegId,
        ploegNaam: p.ploegNaam,
        korpsNaam: p.korpsNaam,
        isOngeplaatst: true,
      }));

    // A ploeg can be both - placed at one niveau and unplaced at another - and the unplaced half
    // is the one worth surfacing, so it wins the deduplication.
    const alles = [...zonderPlaats, ...geplaatst];

    // Sorted on the naam the row shows, which is the one without the voorvoegsel: on the whole
    // naam "JBW Bodegraven" sorts under the J and reads as a B out of place.
    return [...new Map(alles.map((p) => [p.ploegId, p])).values()].sort(
      (a, b) =>
        Number(b.isOngeplaatst) - Number(a.isOngeplaatst) ||
        zonderVoorvoegsel(a.ploegNaam).localeCompare(
          zonderVoorvoegsel(b.ploegNaam),
          "nl",
        ),
    );
  }, [dagen, ongeplaatst, baan.klasseId]);

  // The ploegen that hold a place in this klasse at this niveau, which are the ones there is
  // something to trade with. Scoped to the niveau because a promotie is a second entry at a
  // higher one (ADR-0038): a ploeg's kwalificatieplek is not a place it can give away here.
  const ruilbaar = useMemo(
    () =>
      new Set(
        dagen
          .filter((d) => d.niveauId === dag.niveauId)
          .flatMap((d) => d.banen.filter((b) => b.klasseId === baan.klasseId))
          .flatMap((b) => b.regels)
          .map((r) => r.ploeg?.id)
          .filter((id): id is string => id !== undefined && id !== null),
      ),
    [dagen, dag.niveauId, baan.klasseId],
  );

  const [ploegId, setPloegId] = useState(
    verplaatst?.ploegId ?? bezetter?.id ?? "",
  );
  const [reden, setReden] = useState(verplaatst ? "" : (bezetter?.reden ?? ""));
  const [gekozenActie, setGekozenActie] = useState<number>(bezetKeuzes.ruilen);

  const verkeerdeKlasse =
    verplaatst !== null && verplaatst.klasseId !== baan.klasseId;

  // Somebody else is standing here and is about to be moved out of the way.
  const vervangtBezetter =
    bezetter !== null && ploegId !== "" && ploegId !== bezetter.id;
  const kanRuilen = vervangtBezetter && ruilbaar.has(ploegId);

  // What the save will do with the ploeg standing here. Ruilen needs a plek to trade, so it
  // falls back on its own rather than sending a choice the server would refuse.
  const actie = !vervangtBezetter
    ? bezetKeuzes.weigeren
    : kanRuilen
      ? gekozenActie
      : bezetKeuzes.overschrijven;

  const bezig =
    vastzetten.isPending || losmaken.isPending || verwijderen.isPending;
  const fout = vastzetten.error ?? losmaken.error ?? verwijderen.error;

  // The reden on a cell belongs to the ploeg standing there, so it does not travel to one that
  // is only now being put here. Anything typed since is the beheerder's own and stays.
  const kies = (id: string) => {
    setPloegId(id);

    if (reden === (bezetter?.reden ?? "")) {
      setReden(id === bezetter?.id ? (bezetter?.reden ?? "") : "");
    }
  };

  const gekozenNaam = zonderVoorvoegsel(
    kandidaten.find((p) => p.ploegId === ploegId)?.ploegNaam ??
      verplaatst?.naam ??
      "",
  );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {verplaatst
              ? t("cel.verplaatsTitle", {
                  naam: zonderVoorvoegsel(verplaatst.naam),
                })
              : bezetter
                ? zonderVoorvoegsel(bezetter.naam)
                : t("cel.indelenTitle")}
          </DialogTitle>
          <DialogDescription>
            {t("cel.beschrijving", {
              baan: baan.label,
              tijd: tijd(regel.starttijd),
            })}
            {bezetter
              ? t("cel.vanKorps", {
                  korps: zonderVoorvoegsel(bezetter.korpsNaam),
                })
              : ""}
            {bezetter?.isVastgezet ? t("cel.vastgezetSuffix") : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {verkeerdeKlasse ? (
            // A ploeg plays the klasse its korps entered it in. Moving it onto a baan of another
            // klasse is not a move but a different inschrijving, which is the korps's to make.
            <p className="text-sm text-destructive">
              {t("cel.verkeerdeKlasse", {
                naam: zonderVoorvoegsel(verplaatst.naam),
                klasse: baan.klasseNaam,
              })}
            </p>
          ) : (
            !verplaatst && (
              <PloegVeld
                klasseNaam={baan.klasseNaam}
                kandidaten={kandidaten}
                gekozen={ploegId}
                onKies={kies}
              />
            )
          )}

          {vervangtBezetter && !verkeerdeKlasse && bezetter && (
            <BezetterWisselKeuze
              bezetter={bezetter}
              kanRuilen={kanRuilen}
              actie={actie}
              gekozenNaam={gekozenNaam}
              onKiesActie={setGekozenActie}
            />
          )}

          <Field>
            <FieldLabel htmlFor="reden-cel">{t("cel.reden")}</FieldLabel>
            <Textarea
              id="reden-cel"
              value={reden}
              onChange={(event) => setReden(event.target.value)}
              placeholder={t("cel.redenPlaceholder")}
              rows={2}
            />
            {/*
              Said before the save rather than after it: the reden is what a beheerder six weeks
              from now reads to find out why a row will not move. Worth asking for and not worth
              refusing over - only a beheerder gets this far, and a beheerder correcting a
              planning with a korps on the telephone is exactly who a required field turns away.
            */}
            <p className="text-xs text-muted-foreground">
              {t("cel.redenUitleg")}
            </p>
          </Field>

          {bezetter && !vervangtBezetter && (
            <p className="text-xs text-muted-foreground">
              {t("cel.losmakenUitleg", {
                naam: zonderVoorvoegsel(bezetter.naam),
              })}
            </p>
          )}

          {fout && <p className="text-sm text-destructive">{fout.message}</p>}

          <FormActions
            leading={
              bezetter && !vervangtBezetter ? (
                <CelVerwijderActies
                  bezetter={bezetter}
                  bezig={bezig}
                  onLosmaken={() =>
                    losmaken.mutate(regel.id, { onSuccess: onClose })
                  }
                  onVerwijderen={() =>
                    verwijderen.mutate(regel.id, { onSuccess: onClose })
                  }
                />
              ) : undefined
            }
          >
            {bezetter && !vervangtBezetter && !verplaatst ? (
              <Button
                variant="ghost"
                onClick={() => onVerplaats(bezetter)}
                disabled={bezig}
              >
                {t("cel.verplaatsen")}
              </Button>
            ) : (
              <Button variant="ghost" onClick={onClose} disabled={bezig}>
                {t("cel.annuleren")}
              </Button>
            )}
            <Button
              disabled={bezig || verkeerdeKlasse || !ploegId}
              onClick={() =>
                vastzetten.mutate(
                  {
                    ploegId,
                    wedstrijdbaanId: baan.id,
                    speelschemaregelId: regel.id,
                    reden: reden.trim() || null,
                    bezet: actie,
                  },
                  { onSuccess: onClose },
                )
              }
            >
              {bezig ? t("common:actions.busy") : t("cel.vastzetten")}
            </Button>
          </FormActions>
        </div>
      </DialogContent>
    </Dialog>
  );
}
